import type { IDrawRequest } from './type'

/**
 * How a request's status reads on screen.
 *
 * The status itself is resolved by the backend off the draw that funds the request, so this only
 * decides how it looks. Everything before "Funded" is the lender still deciding; from Funded on
 * the money has moved, which is why those two are the only ones in the brand colour.
 */
const TONE: Record<string, string> = {
  Requested: 'bg-gray-100 text-gray-700',
  'Draw Entered': 'bg-gray-100 text-gray-700',
  'In Review': 'bg-yellow-100 text-yellow-800',
  Declined: 'bg-red-100 text-red-800',
  Withdrawn: 'bg-gray-100 text-gray-700',
  // The lender's own approval of the request, before any draw exists. Distinct from 'Approved',
  // which is the review on a draw somebody has already entered.
  'Approved to Draw': 'bg-blue-100 text-blue-800',
  Approved: 'bg-blue-100 text-blue-800',
  Funded: 'bg-shade-blue/15 text-shade-blue',
  Paid: 'bg-green-100 text-green-800',
}

/**
 * A payload from before the status existed carries none. Showing "Requested" then would be a
 * claim we cannot support - the request may well have been funded - so it stays blank.
 */
export const DrawRequestStatusBadge = ({ request }: { request: IDrawRequest }) => {
  const status = request?.status
  if (!status) return <span className="opacity-60">-</span>

  return (
    <div className="flex flex-col gap-1 items-start">
      <span className={`rounded-full px-2 py-0.5 text-[12px] whitespace-nowrap ${TONE[status] || TONE.Requested}`}>
        {status}
      </span>
      {!!request.reference && <span className="text-[12px] opacity-70">{request.reference}</span>}
      {!!request.declineReason && (
        <span className="text-[12px] text-red-700 max-w-48 whitespace-normal">{request.declineReason}</span>
      )}
    </div>
  )
}
